import { Link } from 'react-router-dom';
import { format8 } from 'utils/number';
import { formatTcWorkspaceDate } from 'utils/dates';
import { useCardPost } from './use-card-resource';
import styles from './expand.module.scss';
import {
  DASH,
  ExpandAccordion,
  ExpandState,
  ExpandTable,
  money,
  num,
} from './common';

// RC revenue line items (POST /royalty_checks/:sfId/queries/line_items), the
// server-driven ES-nested source. Keyed by the check's Salesforce id, serialized
// onto the RC row. One page large enough to hold a check's lines in the card.
interface RcLineItem {
  id: string;
  name: string | null;
  propertyName: string | null;
  product: string | null;
  interestType: string | null;
  productionMonth: string | null;
  price: number | null;
  grossVolume: number | null;
  grossValue: number | null;
  lineItemDecimal: number | null;
  lineItemNetValue: number | null;
  ownerTaxes: number | null;
  ownerAdjustment: number | null;
}

interface RcLineItemsResponse {
  results: RcLineItem[];
  totalCount: number;
}

interface Props {
  salesforceRoyaltyCheckId?: string | null;
  checkTotal?: number | null;
  // Broadcast from the card's Expand-All / Collapse-All control (mirrors Parties/Notes).
  openSignal?: { open: boolean };
}

export const RcExpand = ({
  salesforceRoyaltyCheckId,
  checkTotal,
  openSignal,
}: Props) => {
  const url = salesforceRoyaltyCheckId
    ? `/royalty_checks/${salesforceRoyaltyCheckId}/queries/line_items`
    : '';
  const { loading, data, error } = useCardPost<RcLineItemsResponse>(url, {
    page: 1,
    pageSize: 500,
  });

  if (!salesforceRoyaltyCheckId)
    return <ExpandState>No line items.</ExpandState>;
  if (loading) return <ExpandState>Loading…</ExpandState>;
  if (error) return <ExpandState>Failed to load line items.</ExpandState>;

  const rows = data?.results ?? [];
  // Header reflects the true line-item count; the table itself shows the fetched
  // page (pageSize above), which may be fewer than totalCount for large checks.
  const totalCount = data?.totalCount ?? rows.length;
  if (rows.length === 0) return <ExpandState>No line items.</ExpandState>;

  // The endpoint has no server-side grand total, and caps rows at pageSize; if a
  // check has more line items than that, label the footer as partial so its sum
  // (of only the fetched rows) is never mistaken for the true grand total.
  const truncated = totalCount > rows.length;
  const totalLabel = truncated
    ? `Total (first ${rows.length} of ${totalCount})`
    : `Total (${totalCount})`;

  // Totals row: sum the additive columns over the fetched rows. Price and Decimal
  // are per-unit/ratio figures, so they're left blank (not summed).
  const sum = (pick: (row: RcLineItem) => number | null) =>
    rows.reduce((total, row) => total + (pick(row) ?? 0), 0);

  return (
    <ExpandAccordion
      openSignal={openSignal}
      title={
        <span className={styles.sectionTitleInline}>
          Line Items ({totalCount})
          {checkTotal !== null && checkTotal !== undefined && (
            <span className={styles.total}>
              Check Total: {money(checkTotal)}
            </span>
          )}
        </span>
      }
    >
      <ExpandTable<RcLineItem>
        bordered
        columns={[
          {
            header: 'Line Item',
            cell: (row) => {
              return row.name ? (
                <Link
                  to={`/royalty_checks/${salesforceRoyaltyCheckId}`}
                  className={styles.link}
                >
                  {row.name}
                </Link>
              ) : (
                DASH
              );
            },
          },
          { header: 'Property', cell: (row) => row.propertyName ?? DASH },
          { header: 'Product', cell: (row) => row.product ?? DASH },
          { header: 'Interest', cell: (row) => row.interestType ?? DASH },
          {
            header: 'Prod Month',
            cell: (row) => {
              return row.productionMonth
                ? formatTcWorkspaceDate(row.productionMonth)
                : DASH;
            },
          },
          { header: 'Price', align: 'right', cell: (row) => money(row.price) },
          {
            header: 'Gross Volume',
            align: 'right',
            cell: (row) => num(row.grossVolume),
          },
          {
            header: 'Gross Value',
            align: 'right',
            cell: (row) => money(row.grossValue),
          },
          {
            header: 'Decimal',
            align: 'right',
            cell: (row) => {
              return row.lineItemDecimal === null
                ? DASH
                : format8(row.lineItemDecimal);
            },
          },
          {
            header: 'Net Value',
            align: 'right',
            cell: (row) => money(row.lineItemNetValue),
          },
          {
            header: 'Net Taxes',
            align: 'right',
            cell: (row) => money(row.ownerTaxes),
          },
          {
            header: 'Net Adj.',
            align: 'right',
            cell: (row) => money(row.ownerAdjustment),
          },
        ]}
        rows={rows}
        rowKey={(row) => row.id}
        footer={[
          totalLabel,
          '',
          '',
          '',
          '',
          '',
          num(sum((row) => row.grossVolume)),
          money(sum((row) => row.grossValue)),
          '',
          money(sum((row) => row.lineItemNetValue)),
          money(sum((row) => row.ownerTaxes)),
          money(sum((row) => row.ownerAdjustment)),
        ]}
        pageSize={25}
      />
    </ExpandAccordion>
  );
};
